import type { StudyPdfAnnotation } from './studyApi';

export type PdfPoint = { x: number; y: number };
export type QuarterTurns = 0 | 1 | 2 | 3;

export function rotatePoint(point: PdfPoint, turns: QuarterTurns): PdfPoint {
  if (turns === 1) return { x: 1 - point.y, y: point.x };
  if (turns === 2) return { x: 1 - point.x, y: 1 - point.y };
  if (turns === 3) return { x: point.y, y: 1 - point.x };
  return point;
}

export function unrotatePoint(point: PdfPoint, turns: QuarterTurns): PdfPoint {
  return rotatePoint(point, ((4 - turns) % 4) as QuarterTurns);
}

export function rectangle(start: PdfPoint, end: PdfPoint) {
  return {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
  };
}

export function rotateRectangle(
  rect: { x: number; y: number; width: number; height: number },
  turns: QuarterTurns,
) {
  return rectangle(
    rotatePoint({ x: rect.x, y: rect.y }, turns),
    rotatePoint({ x: rect.x + rect.width, y: rect.y + rect.height }, turns),
  );
}

export function moveGeometry(item: StudyPdfAnnotation, target: PdfPoint): Record<string, unknown> {
  if (item.kind === 'note') return target;
  if (item.kind === 'highlight') {
    const width = Number(item.geometry.width);
    const height = Number(item.geometry.height);
    return {
      ...item.geometry,
      x: Math.max(0, Math.min(1 - width, target.x - width / 2)),
      y: Math.max(0, Math.min(1 - height, target.y - height / 2)),
    };
  }
  const points = item.geometry.points as PdfPoint[];
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const dx = Math.max(-minX, Math.min(1 - maxX, target.x - (minX + maxX) / 2));
  const dy = Math.max(-minY, Math.min(1 - maxY, target.y - (minY + maxY) / 2));
  return {
    ...item.geometry,
    points: points.map((point) => ({ x: point.x + dx, y: point.y + dy })),
  };
}
